/**
 * Snapshot of a form model, for telling whether the user changed it.
 *
 * Values that mean "nothing entered" are equal: null, '', undefined, [] and
 * {} (also nested). Primitives compare as strings, so 5 and '5' (number
 * inputs) are equal. Files compare by name, size and modification time.
 */

const isFile = value => typeof Blob !== 'undefined' && value instanceof Blob

function normalize (value, seen) {
  if (value === null || value === undefined || value === '') return undefined
  if (typeof value !== 'object') return typeof value === 'function' ? undefined : String(value)
  if (isFile(value)) return `file:${value.name || ''}:${value.size}:${value.lastModified || ''}`
  if (value instanceof Date) return `date:${value.getTime()}`
  if (value._isAMomentObject) return `date:${value.valueOf()}`
  if (seen.has(value)) return undefined
  seen.add(value)
  let result
  if (Array.isArray(value)) {
    result = value.length ? value.map(item => normalize(item, seen)) : undefined
  } else {
    const out = {}
    Object.keys(value).sort().forEach(key => {
      const item = normalize(value[key], seen)
      if (item !== undefined) out[key] = item
    })
    result = Object.keys(out).length ? out : undefined
  }
  seen.delete(value)
  return result
}

/** { key: string } for each top-level key of model that holds a value. */
export function snapshotModel (model) {
  const snapshot = {}
  if (!model || typeof model !== 'object') return snapshot
  Object.keys(model).forEach(key => {
    const value = normalize(model[key], new Set())
    if (value !== undefined) snapshot[key] = JSON.stringify(value)
  })
  return snapshot
}

/** True when both snapshots hold the same values, apart from ignoredKeys. */
export function sameSnapshot (a, b, ignoredKeys = []) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  ignoredKeys.forEach(key => keys.delete(key))
  for (const key of keys) {
    if (a[key] !== b[key]) return false
  }
  return true
}
